import React from 'react';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';

import FilterData from '../../../../../shared/assets/ilustrations/FilterData.png';
import StatusLabel from '../../../../../shared/components/StatusLabel/StatusLabel';
import ThemeColors from '../../../../../shared/components/ThemeColors/ThemeColors';
import Titles from '../../../../../shared/components/Titles/Titles';

function translateStatus(status) {
  if (status === 'read') return 'Lido';
  if (status === 'delivered') return 'Entregue';
  if (status === 'failed') return 'Falha';
  if (status === 'received') return 'Recebido';
  return 'Desconhecido';
}

const TableRowsLoader = ({ rowsNum, columns }) =>
  [...Array(rowsNum)].map((_, index) => (
    <TableRow key={index}>
      {columns.map((_, colIndex) => (
        <TableCell
          key={colIndex}
          sx={{
            backgroundColor: index % 2 === 0 ? 'rgb(247 247 252)' : '#ffffff',
            padding: '30px',
            borderBottom: 'none',
          }}
        >
          <Skeleton animation="wave" variant="text" width="100%" />
        </TableCell>
      ))}
    </TableRow>
  ));

export default function IndicatorDynamicTable({
  columns,
  data,
  totalItems,
  page,
  rowsPerPage,
  onPageChange,
  loading,
  selectedLabel,
  onOpenCampaignAnalysis,
  loadingAnalysis,
}) {
  const renderColumn = (column, value) => {
    if (column === 'Status/Entrega') {
      return <StatusLabel status={translateStatus(value)} usingOnNewTable />;
    }
    return value;
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', padding: '20px' }}>
      <TableContainer component={Paper} elevation={0} sx={{ border: 'none' }}>
        <Table sx={{ minWidth: 650, border: 'none' }} aria-label="dynamic table">
	          <TableHead>
	            <TableRow sx={{ borderBottom: 'none' }}>
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '13px', sm: '16px' },
                    borderBottom: 'none',
                    textAlign: index === 0 ? 'left' : 'center',
                    position: { xs: index < 2 ? 'sticky' : 'static', sm: 'static' },
                    zIndex: { xs: index < 1 ? 1 : 'auto', sm: 'auto' },
                    background: ThemeColors.purple900,
                    color: '#fff',
                    paddingTop: '25px',
                    paddingBottom: '25px',
                  }}
                >
                  {column}
                </TableCell>
	              ))}
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '13px', sm: '16px' },
                    borderBottom: 'none',
                    textAlign: 'center',
                    background: ThemeColors.purple900,
                    color: '#fff',
                    paddingTop: '25px',
                    paddingBottom: '25px',
                    minWidth: 180,
                  }}
                >
                  Análise
                </TableCell>
	            </TableRow>
	          </TableHead>
	          <TableBody>
	            {loading ? (
	              <TableRowsLoader rowsNum={100} columns={[...columns, 'Análise']} />
	            ) : data.length === 0 ? (
	              <TableRow>
	                <TableCell colSpan={columns.length + 1} sx={{ borderBottom: 'none', padding: '30px' }}>
                  <Box sx={{ width: { xs: '80%', sm: 'auto' }, mx: 'auto' }}>
                    <Box
                      component="img"
                      src={FilterData}
                      alt="Itens ocultos"
                      sx={{
                        width: { xs: '247px', sm: '494px' },
                        height: { xs: '165px', sm: '330px' },
                        objectFit: 'contain',
                        display: 'block',
                        mx: 'auto',
                      }}
                    />
                    <Box
                      display="flex"
                      sx={{
                        flexDirection: 'column',
                        gap: '10px',
                        justifyContent: { xs: 'start', sm: 'center' },
                        textAlign: { xs: 'start', sm: 'center' },
                      }}
                    >
                      <Titles
                        bold
                        styles={{ fontSize: { xs: '18px', sm: '20px', lg: '24px' } }}
                        color="#8033F4"
                      >
                        Não tem itens a serem exibidos :(
                      </Titles>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <TableRow>
                    {columns.map((column, colIndex) => (
                      <TableCell
                        key={colIndex}
                        sx={{
                          borderBottom: 'none',
                          textAlign: colIndex === 0 ? 'left' : 'center',
                          padding: selectedLabel === 'total_campaigns' ? '30px' : '20px',
                          position: { xs: colIndex < 2 ? 'sticky' : 'static', sm: 'static' },
                          zIndex: { xs: colIndex < 1 ? 1 : 'auto', sm: 'auto' },
                          fontSize: { xs: '13px', sm: '16px' },
                          fontFamily: 'Satoshi-Medium',
                          backgroundColor:
                            column === 'Entregues'
                              ? 'rgba(203, 223, 246, 0.5)'
                              : column === 'Falhas'
                                ? 'rgba(248, 214, 211, 0.5)'
                                : column === 'Lidas'
                                  ? 'rgba(211, 234, 200, 0.5)'
                                  : column === 'Não confirmadas'
                                    ? 'rgba(246, 237, 203, 0.5)'
                                    : column === 'Em espera'
                                      ? 'rgba(229, 229, 229, 0.5)'
                                      : column === 'Desconhecidas'
                                        ? 'rgba(215, 215, 215, 0.5)'
                                        : column === 'Total'
                                          ? 'rgba(221, 212, 255, 0.5)'
                                          : '#fff',
                        }}
                      >
                        {renderColumn(column, row[column])}
                      </TableCell>
	                    ))}
                      <TableCell
                        sx={{
                          borderBottom: 'none',
                          textAlign: 'center',
                          padding: selectedLabel === 'total_campaigns' ? '30px' : '20px',
                          backgroundColor: '#fff',
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={() => onOpenCampaignAnalysis(row)}
                          disabled={row.Campanha === 'Total' || loadingAnalysis}
                          sx={{
                            borderColor: ThemeColors.purple500,
                            color: ThemeColors.purple700,
                            textTransform: 'none',
                            fontWeight: 700,
                            minWidth: 130,
                            '&:hover': {
                              borderColor: ThemeColors.purple700,
                              backgroundColor: '#F4EEFF',
                            },
                          }}
                        >
                          Ver análise
                        </Button>
                      </TableCell>
	                  </TableRow>
	                  <TableRow>
	                    <TableCell
	                      colSpan={columns.length + 1}
	                      sx={{ height: '5px', padding: 0, borderBottom: 'none', backgroundColor: '#F4F5FF' }}
	                    />
	                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalItems > 0 && !loading && (
        <TablePagination
          rowsPerPageOptions={[10]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
        />
      )}
    </Box>
  );
}
